import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Sparkles, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';

const ChiSiamo = () => {
  const { lang } = useLanguage();
  const c = t('chiSiamo', lang);
  const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

  return (
    <div className="bg-white pt-32 pb-0" data-testid="chi-siamo-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn} className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0A1312] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{c.title}</h1>
          <p className="text-xl text-[#65c3ba] font-semibold" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{c.slogan}</p>
        </motion.div>

        {/* Intro with image */}
        <motion.div {...fadeIn} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <p className="text-lg text-[#0A1312] mb-6 leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>
              {c.intro1}
            </p>
            <p className="text-lg text-[#0A1312] leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>
              {c.intro2}
            </p>
          </div>
          <div className="rounded-sm overflow-hidden shadow-lg">
            <img
              src="https://customer-assets.emergentagent.com/job_homewise-ticino/artifacts/lc029vld_ChatGPT%20Image%203%20mag%202026%2C%2001_02_03.png"
              alt="Consulenza immobiliare"
              className="w-full h-80 object-cover"
            />
          </div>
        </motion.div>

        {/* Mission section */}
        <motion.div {...fadeIn} transition={{ delay: 0.1 }} className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 rounded-sm overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_homewise-ticino/artifacts/t4fgt01o_pexels-image-hunter-281453274-26925314.jpg"
                alt="Appartamento con vista"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>
                {c.missionTitle}
              </h2>
              <p className="text-lg text-[#0A1312] mb-4 leading-relaxed italic" style={{fontFamily: "'Satoshi', sans-serif"}}>
                {c.missionQuote1}
              </p>
              <p className="text-lg text-[#65c3ba] font-semibold mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>
                {c.missionQuote2}
              </p>
              <p className="text-lg text-[#0A1312] leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>
                {c.missionText}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Goals */}
        <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {c.goals.map((goal, idx) => {
              const icons = [TrendingUp, Users, Sparkles];
              const Icon = icons[idx];
              return (
                <div key={`goal-${goal.slice(0,15)}`} className="bg-[#F7F9F9] p-8 border border-[#E2E8F0] rounded-sm text-center" data-testid={`goal-${idx}`}>
                  <Icon className="text-[#65c3ba] mx-auto mb-4" size={36} strokeWidth={1.5} />
                  <p className="text-[#0A1312] text-lg font-medium" style={{fontFamily: "'Satoshi', sans-serif"}}>{goal}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Approach note */}
        <motion.div {...fadeIn} transition={{ delay: 0.3 }} className="max-w-3xl mx-auto text-center mb-16">
          <p className="text-lg text-[#0A1312] leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>
            {c.approachText}
          </p>
        </motion.div>
      </div>

      {/* CTA full width */}
      <motion.div {...fadeIn} transition={{ delay: 0.4 }} className="relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1694501898258-39db571d4e90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxjb3p5JTIwYmVkcm9vbSUyMGFwYXJ0bWVudCUyMHRlcnJhY2UlMjB2aWV3JTIwc3Vuc2V0fGVufDB8fHx8MTc3Nzc2MDM2OHww&ixlib=rb-4.1.0&q=85')", backgroundAttachment: 'fixed' }} />
        <div className="absolute inset-0 bg-[#3e716c]/80" />
        <div className="relative z-10 py-16 px-4 text-white">
          <h2 className="text-2xl font-bold mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{c.ctaTitle}</h2>
          <p className="text-white/70 mb-8" style={{fontFamily: "'Satoshi', sans-serif"}}>{c.ctaText}</p>
          <Link to="/valutazione-gratuita">
            <Button className="bg-[#65c3ba] text-white hover:bg-white hover:text-[#3e716c] rounded-sm px-8 py-6 text-lg">{c.ctaBtn}</Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ChiSiamo;
